import {
  VuexModule,
  Module,
  Action,
  getModule,
  Mutation,
} from "vuex-module-decorators";
import store from "./index";
import cabinetAuthApi from "@/services/api/auth.api";
import { UserService } from "@/services/storage.service";
import {
  CABINET_BASE_URL,
  ROLE_CLAIMANT,
  ROLE_EMPLOYER,
  ROLE_EXTERNALUSERS,
  ROLE_STORONA_IP,
  ROLE_STORONA_IP_FIRST_LEADER,
} from "@/config/const";
import baseModule from "./base.module";
import { RegionTreeItem, UserData } from "@/types";
import { AuthParams, IinBinParams } from "@/services/api/types";
import userregistrationApi from "@/services/api/userregistration.api";

@Module({ namespaced: true, dynamic: true, store, name: "auth" })
class AuthModule extends VuexModule {
  public roles = UserService.getRoles();

  isEmployerNotActive = false;
  regions: RegionTreeItem[] = [];
  regionsLoading = false;

  @Mutation
  setIsEmployerNotActive(isEmployerNotActive: boolean) {
    this.isEmployerNotActive = isEmployerNotActive;
  }

  @Mutation
  private setRoles(roles: string[]) {
    this.roles = roles;
    UserService.setRoles(roles);
  }

  @Mutation
  setRegions(regions: RegionTreeItem[]) {
    this.regions = regions;
  }

  @Mutation
  setRegionsLoading(regionsLoading: boolean) {
    this.regionsLoading = regionsLoading;
  }

  get loggedIn() {
    return !!this.roles.length;
  }

  get hasAccessTo() {
    return (roles?: [string]) => {
      return roles?.length
        ? roles.some((routeRole: string) => this.roles.includes(routeRole))
        : true;
    };
  }

  get isEmployer() {
    return this.roles.includes(ROLE_EMPLOYER);
  }

  get isClaimant() {
    return this.roles.includes(ROLE_CLAIMANT);
  }

  get onlyStoronaIp() {
    return this.roles.includes(ROLE_STORONA_IP);
  }

  get storonaIpLeader() {
    return this.roles.includes(ROLE_STORONA_IP_FIRST_LEADER);
  }

  @Action
  public async getEmployerStatus() {
    if (await cabinetAuthApi.getEmployerStatus()) {
      this.setIsEmployerNotActive(false);
    } else {
      baseModule.showMessage({ message: "noAccess", color: "error" });
      this.setIsEmployerNotActive(true);
    }
  }

  @Action
  public async redirectToWSOLoginPage() {
    this.setRoles([]);
    window.location.href = `${CABINET_BASE_URL}checksession`;
  }

  @Action
  public async login(p: AuthParams) {
    await cabinetAuthApi.auth(p);
    const roles: string[] = await cabinetAuthApi.getRoles();
    if (roles) {
      const permittedRoles = [
        ROLE_STORONA_IP,
        ROLE_STORONA_IP_FIRST_LEADER,
        ROLE_EXTERNALUSERS,
        ROLE_EMPLOYER,
      ];
      if (!roles.filter((role) => permittedRoles.includes(role)).length) {
        // baseModule.showMessage({ message: "noAccess", color: "error" });
        return;
      }
      this.setRoles(roles);
    }
  }

  @Action
  public async logout() {
    this.setRoles([]);

    const link = document.createElement("a") as HTMLAnchorElement;
    link.href = `${CABINET_BASE_URL}rest/user/logout`;

    link.click();
  }

  @Action
  public async getUser(p: IinBinParams) {
    return await userregistrationApi.user(p);
  }

  @Action
  public async getRegions() {
    try {
      const res = await userregistrationApi.region();
      this.setRegions(res);
    } catch (e) {
      console.error(e);
    }
  }

  @Action
  public async getXmlToSign(p: UserData) {
    const address = {
      id: p.address?.region?.id,
      guid: p.address?.region?.guid,
      address: p.address?.address,
    };

    p.address = address;

    try {
      const res = await userregistrationApi.xml(p);
      return res;
    } catch (e) {
      console.error(e);
    }
  }

  @Action
  public async register(xmlData: string) {
    try {
      await userregistrationApi.register(xmlData);
      return true;
    } catch (e) {
      console.log(e);
    }
  }

  @Action
  public async resetPassword(p: IinBinParams) {
    try {
      baseModule.setProgress(true);
      await userregistrationApi.password(p);
      return true;
    } catch (e) {
      console.log(e);
    } finally {
      baseModule.setProgress(false);
    }
  }
}

export default getModule(AuthModule);
